import type {
    DayOfWeek,
    Language,
    MealPlan,
    MealType,
    PlannedMeal,
    Recipe,
    Reminder,
    WeekStartDay,
} from '../types';
import { DAY_TRANSLATIONS } from '../types';
import { getTranslation } from './translations';
import { buildScaledMeatItems } from './meatCalculator';

const MEAL_TYPE_KEYS: Record<MealType, 'morning' | 'noon' | 'evening'> = {
    Breakfast: 'morning',
    Lunch: 'noon',
    Dinner: 'evening',
};

export function getWeekStartDate(offset: number, weekStartDay: WeekStartDay = 'Monday'): Date {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const startDayIndex = weekStartDay === 'Sunday' ? 0 : 1;
    let diff = now.getDay() - startDayIndex;
    if (diff < 0) diff += 7;
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - diff + offset * 7);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
}

export function formatDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function getWeekKey(offset: number, weekStartDay: WeekStartDay = 'Monday'): string {
    return formatDateKey(getWeekStartDate(offset, weekStartDay));
}

function parseWeekKey(weekKey: string): Date {
    const [y, m, d] = weekKey.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setHours(0, 0, 0, 0);
    return date;
}

function getMealDate(weekKey: string, day: DayOfWeek, orderedDays: readonly DayOfWeek[]): Date | null {
    const dayIndex = orderedDays.indexOf(day);
    if (dayIndex < 0) return null;
    const weekStart = parseWeekKey(weekKey);
    const mealDate = new Date(weekStart);
    mealDate.setDate(weekStart.getDate() + dayIndex);
    mealDate.setHours(0, 0, 0, 0);
    return mealDate;
}

function isSameCalendarDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate()
    );
}

function isTomorrow(mealDate: Date, today: Date): boolean {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return isSameCalendarDay(mealDate, tomorrow);
}

function recipeHasMeat(recipe: Recipe): boolean {
    return recipe.ingredients.some((ing) => ing.isMeat);
}

function formatReminderDate(date: Date, language?: Language): string {
    const locale = language === 'he' ? 'he-IL' : language === 'ar' ? 'ar' : language || 'en';
    return date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function buildDefrostReminders(params: {
    mealPlans: Record<string, MealPlan>;
    recipes: Recipe[];
    orderedDays: readonly DayOfWeek[];
    language?: Language;
    now?: Date;
}): Reminder[] {
    const { mealPlans, recipes, orderedDays, language, now = new Date() } = params;
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);

    const reminders: Reminder[] = [];

    for (const [weekKey, plan] of Object.entries(mealPlans)) {
        for (const day of orderedDays) {
            const meals = plan[day];
            if (!meals?.length) continue;

            const mealDate = getMealDate(weekKey, day, orderedDays);
            if (!mealDate || !isTomorrow(mealDate, today)) continue;

            for (const meal of meals) {
                if (meal.isDeleted) continue;
                if (meal.sourcingStatus === 'pantry') continue;

                const recipe = recipes.find((r) => r.id === meal.recipeId);
                if (!recipe || recipe.isDeleted || !recipeHasMeat(recipe)) continue;

                const meatItems = buildScaledMeatItems(recipe, meal.plannedServings);
                if (meatItems.length === 0) continue;

                const mealType = meal.mealType || 'Dinner';
                const mealLabel = t(MEAL_TYPE_KEYS[mealType]);
                const dayLabel = t(DAY_TRANSLATIONS[day]);

                reminders.push({
                    id: `defrost-${weekKey}-${day}-${meal.id}`,
                    type: 'defrost',
                    date: formatReminderDate(mealDate, language),
                    text: t('defrostForMeal').replace('{meal}', mealLabel).replace('{day}', dayLabel),
                    meatItems,
                });
            }
        }
    }

    return reminders.sort((a, b) => a.date.localeCompare(b.date));
}
